// src/components/ClientEDNDetail.js
// Client B2B — /clients-instructions/:clientId. One merchant: what its tickets
// look like (GET /clients-edn/{id}/stats), what needs a person on its
// configuration, the tickets themselves, the configuration (read-only
// preview or edit form — the same field table as the create modal,
// ClientB2BFields.js) and the ERP API access.
// Views (?tab=): overview · tickets · configuration · api. ?edit=1 opens the
// edit form. The ticket list keeps its historical URL params (?tpage,
// ?tstatus, ?tcarrier — a link with one of them opens the tickets view).
// API: GET /clients-edn (the list, the page picks its client), PUT
// /clients-edn/{id}, GET /clients-edn/{id}/tickets|stats, GET/POST/DELETE
// /clients-edn/{id}/api-access, GET /carriers, GET /souffrance-config.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BarChart3, Check, ChevronRight, CircleAlert, Copy, KeyRound, LayoutDashboard, ListChecks, Pencil, Plug,
  RefreshCw, Search, Settings2, Sparkles, Tags, Ticket, TriangleAlert, Truck, X,
} from 'lucide-react';
import {
  Badge, Banner, Card, ConfirmDialog, EmptyState, Kpi, Meter, Pagination, Tabs, Toast, initialsOf,
} from '../design/ui';
import {
  ALL_STATUS_MAPPING, getResolutionActor, getStatusLabel, RESOLUTION_ACTOR,
} from '../constants/ticketStatuses';
import { ClientB2BForm, ClientB2BPreview } from './ClientB2BFields';
import {
  SOUFFRANCE_LANGUAGES as LANGUAGES, buildUpdatePayload, clientActivity, clientAttention, detectionCounts,
  formFromClient, statusTone,
} from './clientsB2B';
import { formatCount, formatDay, formatPercent, formatRelative, formatStamp, nextParams } from './clientsShared';
import './ClientEDNDetail.css';

// Kept for compatibility: other modules imported the list from this page.
export const SOUFFRANCE_LANGUAGES = LANGUAGES;

const TICKETS_PAGE_SIZE = 50;
const TABS = ['overview', 'tickets', 'configuration', 'api'];
const TICKET_PARAMS = ['tpage', 'tstatus', 'tcarrier'];

const ClientEDNDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const tab = TABS.includes(tabParam)
    ? tabParam
    : searchParams.get('edit') === '1'
      ? 'configuration'
      : TICKET_PARAMS.some((key) => searchParams.get(key)) ? 'tickets' : 'overview';
  const editing = searchParams.get('edit') === '1';
  const pageParam = parseInt(searchParams.get('tpage') || '1', 10);
  const ticketsPage = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;
  const statusFilter = searchParams.get('tstatus') || '';
  const carrierFilter = searchParams.get('tcarrier') || '';

  const updateParams = useCallback((updates, { replace = true } = {}) => {
    setSearchParams((previous) => nextParams(previous, updates), { replace });
  }, [setSearchParams]);

  const authHeaders = useMemo(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
    if (!token) return null;
    return { Authorization: `Bearer ${token}` };
  }, []);

  useEffect(() => {
    if (!authHeaders) navigate('/');
  }, [authHeaders, navigate]);

  const [client, setClient] = useState(null);
  const [clientLoading, setClientLoading] = useState(true);
  const [clientError, setClientError] = useState(null);

  const [stats, setStats] = useState(undefined); // undefined = loading, null = unavailable
  const [carriers, setCarriers] = useState(null);
  const [generalLanguage, setGeneralLanguage] = useState(null);

  const [tickets, setTickets] = useState(null); // null = loading
  const [ticketsError, setTicketsError] = useState(null);
  const [ticketsHasMore, setTicketsHasMore] = useState(false);
  const [carrierInput, setCarrierInput] = useState(carrierFilter);

  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [apiAccess, setApiAccess] = useState(null);
  const [apiAccessLoading, setApiAccessLoading] = useState(true);
  const [apiAccessBusy, setApiAccessBusy] = useState(false);
  const [apiAccessError, setApiAccessError] = useState(null);
  const [generatedApiKey, setGeneratedApiKey] = useState(null);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);

  const [confirmation, setConfirmation] = useState(null); // 'revoke' | 'rotate'
  const [toast, setToast] = useState(null);
  useEffect(() => {
    if (!toast || toast.tone === 'danger') return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const apiBaseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  // ---- fetch ------------------------------------------------------------------

  const fetchClient = useCallback(async () => {
    if (!authHeaders) return;
    setClientLoading(true);
    setClientError(null);
    try {
      const response = await axios.get('/api/logistics/clients-edn', { headers: authHeaders });
      const found = (Array.isArray(response.data) ? response.data : []).find((c) => c.id === clientId);
      if (found) {
        setClient(found);
      } else {
        setClientError(t('clientEDNDetail.notFound', 'Client introuvable'));
      }
    } catch (err) {
      setClientError(err.response?.data?.detail || err.message || t('clientsUi.detail.loadError', 'Impossible de charger ce client.'));
    } finally {
      setClientLoading(false);
    }
  }, [authHeaders, clientId, t]);

  const fetchStats = useCallback(async () => {
    if (!authHeaders) return;
    setStats(undefined);
    try {
      const response = await axios.get(`/api/logistics/clients-edn/${clientId}/stats`, { headers: authHeaders });
      setStats(response.data || null);
    } catch {
      setStats(null);
    }
  }, [authHeaders, clientId]);

  const fetchCarriers = useCallback(async () => {
    if (!authHeaders) return;
    try {
      const response = await axios.get('/api/logistics/carriers', { headers: authHeaders });
      setCarriers(Array.isArray(response.data) ? response.data : []);
    } catch {
      setCarriers([]);
    }
  }, [authHeaders]);

  // The general souffrance language, to say what « par défaut » means here.
  const fetchGeneralLanguage = useCallback(async () => {
    if (!authHeaders) return;
    try {
      const response = await axios.get('/api/logistics/souffrance-config', { headers: authHeaders });
      setGeneralLanguage(response.data?.language || null);
    } catch {
      setGeneralLanguage(null);
    }
  }, [authHeaders]);

  const fetchApiAccess = useCallback(async () => {
    if (!authHeaders) return;
    setApiAccessLoading(true);
    setApiAccessError(null);
    try {
      const response = await axios.get(`/api/logistics/clients-edn/${clientId}/api-access`, { headers: authHeaders });
      setApiAccess(response.data);
    } catch (err) {
      setApiAccess(null);
      setApiAccessError(err.response?.data?.detail || err.message || t('clientsUi.api.loadError', 'Impossible de charger l’accès API.'));
    } finally {
      setApiAccessLoading(false);
    }
  }, [authHeaders, clientId, t]);

  const fetchTickets = useCallback(async () => {
    if (!authHeaders) return;
    setTickets(null);
    setTicketsError(null);
    try {
      const params = { skip: (ticketsPage - 1) * TICKETS_PAGE_SIZE, limit: TICKETS_PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      if (carrierFilter) params.carrier = carrierFilter;
      const response = await axios.get(`/api/logistics/clients-edn/${clientId}/tickets`, { headers: authHeaders, params });
      const payload = Array.isArray(response.data) ? response.data : [];
      setTickets(payload);
      setTicketsHasMore(payload.length === TICKETS_PAGE_SIZE);
    } catch (err) {
      setTicketsError(err.response?.data?.detail || err.message || t('clientsUi.tickets.loadError', 'Impossible de charger les tickets.'));
      setTickets([]);
      setTicketsHasMore(false);
    }
  }, [authHeaders, clientId, statusFilter, carrierFilter, ticketsPage, t]);

  useEffect(() => {
    fetchClient();
    fetchStats();
    fetchCarriers();
    fetchApiAccess();
    fetchGeneralLanguage();
  }, [fetchClient, fetchStats, fetchCarriers, fetchApiAccess, fetchGeneralLanguage]);

  // The ticket list is read when its view is open.
  useEffect(() => {
    if (tab === 'tickets') fetchTickets();
  }, [tab, fetchTickets]);

  // Carrier filter: typed freely, sent after a pause.
  useEffect(() => { setCarrierInput(carrierFilter); }, [carrierFilter]);
  useEffect(() => {
    if (carrierInput.trim() === carrierFilter) return undefined;
    const timer = setTimeout(() => updateParams({ tcarrier: carrierInput.trim(), tpage: null }), 400);
    return () => clearTimeout(timer);
  }, [carrierInput, carrierFilter, updateParams]);

  // Edit form: opened by ?edit=1, filled from the stored client.
  useEffect(() => {
    if (editing && client && !editForm) setEditForm(formFromClient(client));
    if (!editing && editForm) {
      setEditForm(null);
      setSaveError(null);
    }
  }, [editing, client, editForm]);

  // ---- actions ------------------------------------------------------------------

  const setTab = (next) => updateParams({ tab: next === 'overview' ? null : next });
  const openTicket = (ticketId) => navigate(`/tickets/${ticketId}/messages`);
  const openTicketsFiltered = (filters = {}) => updateParams({ tab: 'tickets', tpage: null, tstatus: null, tcarrier: null, ...filters });
  const startEdit = () => updateParams({ tab: 'configuration', edit: '1' });
  // Leaving the form stays on the configuration it was editing.
  const cancelEdit = () => updateParams({ edit: null, tab: 'configuration' });

  const handleSave = async (event) => {
    if (event) event.preventDefault();
    if (!authHeaders || !editForm) return;
    if (!editForm.name.trim()) {
      setSaveError(t('clientsInstructions.nameRequired', 'Le nom du client est obligatoire.'));
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const response = await axios.put(`/api/logistics/clients-edn/${clientId}`, buildUpdatePayload(editForm), { headers: authHeaders });
      setClient(response.data);
      updateParams({ edit: null, tab: 'configuration' });
      setToast({ tone: 'success', text: t('clientsUi.detail.saved', 'Fiche de {{name}} enregistrée.', { name: response.data?.name || editForm.name }) });
    } catch (err) {
      setSaveError(err.response?.data?.detail || err.message || t('clientsUi.detail.saveError', 'Échec de l’enregistrement.'));
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    fetchClient();
    fetchStats();
    fetchApiAccess();
    if (tab === 'tickets') fetchTickets();
  };

  const handleGenerateApiKey = async () => {
    if (!authHeaders) return;
    setApiAccessBusy(true);
    setApiAccessError(null);
    setApiKeyCopied(false);
    try {
      const response = await axios.post(`/api/logistics/clients-edn/${clientId}/api-access`, {}, { headers: authHeaders });
      setGeneratedApiKey(response.data.api_key);
      setApiAccess({
        client_edn_id: response.data.client_edn_id,
        enabled: response.data.enabled,
        key_prefix: response.data.key_prefix,
        created_at: response.data.created_at,
        last_used_at: response.data.last_used_at,
        revoked_at: response.data.revoked_at,
      });
    } catch (err) {
      setApiAccessError(err.response?.data?.detail || err.message || t('clientsUi.api.generateError', 'Impossible de créer la clé.'));
    } finally {
      setApiAccessBusy(false);
      setConfirmation(null);
    }
  };

  const handleRevokeApiKey = async () => {
    if (!authHeaders) return;
    setApiAccessBusy(true);
    setApiAccessError(null);
    try {
      const response = await axios.delete(`/api/logistics/clients-edn/${clientId}/api-access`, { headers: authHeaders });
      setApiAccess(response.data);
      setGeneratedApiKey(null);
      setToast({ tone: 'success', text: t('clientsUi.api.revoked', 'Accès API révoqué.') });
    } catch (err) {
      setApiAccessError(err.response?.data?.detail || err.message || t('clientsUi.api.revokeError', 'Impossible de révoquer la clé.'));
    } finally {
      setApiAccessBusy(false);
      setConfirmation(null);
    }
  };

  const handleCopyApiKey = async () => {
    if (!generatedApiKey) return;
    try {
      await navigator.clipboard.writeText(generatedApiKey);
      setApiKeyCopied(true);
      setTimeout(() => setApiKeyCopied(false), 2000);
    } catch {
      setApiKeyCopied(false);
    }
  };

  // ---- derived ----------------------------------------------------------------------

  const activity = useMemo(() => clientActivity(stats), [stats]);
  const attention = useMemo(() => clientAttention({ client, carriers, apiAccess, activity }), [client, carriers, apiAccess, activity]);

  if (!authHeaders) return null;

  // Total of the current ticket list when the stats can tell it (no carrier
  // filter: the API has no count).
  const ticketsTotal = (() => {
    if (!activity || carrierFilter) return null;
    if (!statusFilter) return activity.total;
    const row = activity.byStatus.find((entry) => entry.status === statusFilter);
    return row ? row.count : 0;
  })();

  const bucketLabels = {
    new: t('clientsUi.buckets.new', 'Nouveaux'),
    progress: t('clientsUi.buckets.progress', 'En traitement'),
    waiting: t('clientsUi.buckets.waiting', 'En attente'),
    resolved: t('clientsUi.buckets.resolved', 'Résolus'),
    other: t('clientsUi.buckets.other', 'Autres'),
  };

  // ---- zones --------------------------------------------------------------------------

  const renderHero = () => {
    if (clientLoading && !client) {
      return (
        <section className="u-card client-b2b-v2__hero" aria-busy="true">
          <div className="client-b2b-v2__identity">
            <span className="u-skeleton client-b2b-v2__avatar-skeleton" />
            <div className="client-b2b-v2__identity-text">
              <span className="u-skeleton client-b2b-v2__line-skeleton" />
              <span className="u-skeleton client-b2b-v2__title-skeleton" />
            </div>
          </div>
        </section>
      );
    }
    if (!client) return null;
    const detection = detectionCounts(client.emails);
    return (
      <section className="u-card client-b2b-v2__hero">
        <div className="client-b2b-v2__identity">
          <span className="u-avatar u-avatar--lg" aria-hidden="true">{initialsOf(client.name)}</span>
          <div className="client-b2b-v2__identity-text">
            <nav className="u-breadcrumb" aria-label={t('clientsUi.breadcrumb', 'Fil d’Ariane')}>
              <Link to="/clients-instructions">{t('clientsInstructions.pageTitle', 'Clients B2B')}</Link>
              <ChevronRight className="client-b2b-v2__crumb-sep" aria-hidden="true" />
            </nav>
            <h1 className="u-page-title">{client.name}</h1>
            <div className="client-b2b-v2__meta">
              {client.client_id && <span className="u-code">{client.client_id}</span>}
              {client.automation
                ? <Badge tone="success" dot>{t('clientEDNDetail.automationOn', 'Automatisation active')}</Badge>
                : <Badge>{t('clientEDNDetail.automationOff', 'Automatisation désactivée')}</Badge>}
              <span>
                {detection.total
                  ? t('clientsUi.detail.recognisedBy', 'reconnu par {{count}} adresses ou domaines', { count: detection.total })
                  : t('clientsUi.detail.notRecognised', 'aucune adresse de reconnaissance')}
              </span>
              {client.updated_at && <span>{t('clientsUi.detail.updated', 'modifié le {{date}}', { date: formatDay(client.updated_at) })}</span>}
            </div>
          </div>
          <div className="client-b2b-v2__actions">
            <button
              type="button"
              className="u-btn u-btn--secondary u-btn--icon"
              onClick={handleRefresh}
              title={t('clientEDNDetail.refresh', 'Actualiser')}
              aria-label={t('clientEDNDetail.refresh', 'Actualiser')}
            >
              <RefreshCw aria-hidden="true" />
            </button>
            <Link className="u-btn u-btn--secondary" to={`/statistics?client=${client.id}`}>
              <BarChart3 aria-hidden="true" />
              {t('clientEDNDetail.statistics', 'Statistiques')}
            </Link>
            {!editing && (
              <button type="button" className="u-btn u-btn--primary" onClick={startEdit}>
                <Pencil aria-hidden="true" />
                {t('clientsInstructions.editClient', 'Modifier le client')}
              </button>
            )}
          </div>
        </div>
        <Tabs
          label={client.name}
          active={tab}
          onChange={setTab}
          tabs={[
            { key: 'overview', label: t('clientsUi.detail.tabOverview', 'Vue d’ensemble'), icon: LayoutDashboard },
            { key: 'tickets', label: t('clientEDNDetail.tickets', 'Tickets'), icon: Ticket, count: activity ? formatCount(activity.total) : undefined },
            { key: 'configuration', label: t('clientEDNDetail.configuration', 'Configuration'), icon: Settings2 },
            {
              key: 'api',
              label: t('clientsUi.detail.tabApi', 'Accès API'),
              icon: Plug,
              count: apiAccess?.enabled ? t('clientEDNDetail.apiActive', 'Actif') : undefined,
            },
          ]}
        />
      </section>
    );
  };

  const attentionText = (item) => {
    switch (item.kind) {
      case 'team':
        return {
          title: t('clientsUi.detail.attnTeam', '{{count}} tickets attendent votre équipe', { count: item.count }),
          detail: t('clientsUi.detail.attnTeamDetail', 'Action requise ou erreur de traitement : l’agent ne peut pas avancer seul.'),
        };
      case 'noemail':
        return {
          title: t('clientsUi.detail.attnNoEmail', 'Aucune adresse ni domaine de reconnaissance'),
          detail: t('clientsUi.detail.attnNoEmailDetail', 'Ses e-mails entrants ne lui sont pas rattachés automatiquement : ajoutez son domaine (ex. marque.com).'),
        };
      case 'noinstr':
        return {
          title: t('clientsUi.detail.attnNoInstr', 'Automatisé sans consigne propre'),
          detail: t('clientsUi.detail.attnNoInstrDetail', 'L’agent agit seul sur ses tickets avec vos seules consignes générales.'),
        };
      case 'stale':
        return {
          title: t('clientsUi.detail.attnStale', 'Exclut un transporteur que vous n’avez plus'),
          detail: t('clientsUi.detail.attnStaleDetail', 'Exclusion sans effet : {{carriers}}. Décochez-la dans la configuration.', { carriers: item.carriers.join(', ') }),
        };
      case 'alias':
        return {
          title: t('clientsUi.detail.attnAlias', 'L’IA a ajouté l’alias « {{alias}} » le {{date}}', { alias: item.entry.alias, date: formatDay(item.entry.at) }),
          detail: t('clientsUi.detail.attnAliasDetail', 'Enseigne vérifiée sur {{carrier}} (colis {{tracking}}). Retirez-le s’il ne correspond pas à ce client.', {
            carrier: item.entry.carrier || t('clientEDNDetail.aliasProvenanceCarrier', 'le portail transporteur'),
            tracking: item.entry.verified_tracking_number || '—',
          }),
        };
      case 'apiunused':
        return {
          title: t('clientsUi.detail.attnApiUnused', 'La clé API n’a jamais été utilisée'),
          detail: t('clientsUi.detail.attnApiUnusedDetail', 'Créée le {{date}} : l’ERP de ce client ne l’a pas encore appelée.', { date: formatDay(apiAccess?.created_at) || '—' }),
        };
      default:
        return { title: item.kind, detail: '' };
    }
  };

  const renderAttention = () => {
    if (!attention.length) return null;
    return (
      <Card
        icon={TriangleAlert}
        iconTone="warning"
        title={t('clientsUi.attention.title', 'À vérifier')}
        badge={<Badge tone="warning">{attention.length}</Badge>}
        flush
        className="client-b2b-v2__attention"
      >
        <ul className="u-list">
          {attention.map((item) => {
            const text = attentionText(item);
            return (
              <li className="u-row" key={item.key}>
                <Badge tone={item.tone} dot>
                  {item.tone === 'danger'
                    ? t('clientsUi.attention.critical', 'Bloquant')
                    : item.tone === 'info' ? t('clientsUi.attention.review', 'À relire') : t('clientsUi.attention.watch', 'À surveiller')}
                </Badge>
                <div className="u-row__main">
                  <div className="u-row__title">{text.title}</div>
                  <div className="u-row__meta u-row__meta--wrap">{text.detail}</div>
                </div>
                <div className="u-row__side">
                  <button
                    type="button"
                    className="u-btn u-btn--secondary u-btn--sm"
                    onClick={() => (item.status ? openTicketsFiltered({ tstatus: item.status }) : setTab(item.tab))}
                  >
                    {t('clientsUi.see', 'Voir')}
                    <ChevronRight aria-hidden="true" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </Card>
    );
  };

  const renderKpis = () => {
    if (stats === undefined) {
      return (
        <div className="u-kpi-row" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton client-b2b-v2__kpi-skeleton" />)}
        </div>
      );
    }
    if (!activity || activity.total === 0) return null;
    const pct = (value) => formatPercent(activity.total ? value / activity.total : null);
    return (
      <div className="u-kpi-row client-b2b-v2__kpis">
        <Kpi
          icon={ListChecks}
          tone={activity.open ? undefined : 'success'}
          label={t('clientsUi.detail.kpiOpen', 'En cours')}
          value={formatCount(activity.open)}
          unit={`/ ${formatCount(activity.total)}`}
          context={t('clientsUi.detail.kpiOpenContext', '{{pct}} de ses tickets', { pct: pct(activity.open) })}
          meter={{ value: activity.open, max: activity.total, label: t('clientsUi.detail.kpiOpen', 'En cours') }}
          onClick={() => openTicketsFiltered()}
        />
        <Kpi
          icon={Truck}
          tone={activity.waitingCarrier ? 'warning' : 'neutral'}
          label={t('clientsUi.detail.kpiCarrier', 'Attente transporteur')}
          value={formatCount(activity.waitingCarrier)}
          context={t('clientsUi.detail.kpiCarrierContext', 'tickets attendent sa réponse')}
          onClick={() => openTicketsFiltered({ tstatus: 'waiting_carrier_response' })}
        />
        <Kpi
          icon={Check}
          tone="success"
          label={t('clientsUi.detail.kpiResolved', 'Résolus')}
          value={pct(activity.resolved)}
          context={t('clientsUi.detail.kpiResolvedContext', '{{count}} tickets résolus', { count: formatCount(activity.resolved) })}
          meter={{ value: activity.resolved, max: activity.total, tone: 'success', label: t('clientsUi.detail.kpiResolved', 'Résolus') }}
        />
        {activity.topCarrier && (
          <Kpi
            icon={Truck}
            tone="neutral"
            label={t('clientsUi.detail.kpiTopCarrier', 'Transporteur principal')}
            value={activity.topCarrier.carrier_name}
            context={t('clientsUi.detail.kpiTopCarrierContext', '{{count}} tickets · {{pct}}', { count: formatCount(activity.topCarrier.count), pct: pct(activity.topCarrier.count) })}
            onClick={() => openTicketsFiltered({ tcarrier: activity.topCarrier.carrier_name })}
          />
        )}
      </div>
    );
  };

  const renderMeterRows = (rows, max, nameKey, onPick) => (
    <ul className="u-list">
      {rows.slice(0, 6).map((row) => (
        <li key={row[nameKey]} className="u-row client-b2b-v2__meter-row">
          <span className="client-b2b-v2__meter-name" title={row[nameKey]}>{row[nameKey]}</span>
          <Meter value={Number(row.count)} max={max} label={row[nameKey]} />
          <span className="client-b2b-v2__meter-count u-num">{formatCount(row.count)}</span>
          {onPick ? (
            <button
              type="button"
              className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
              onClick={() => onPick(row[nameKey])}
              aria-label={t('clientsUi.detail.seeTicketsOf', 'Voir les tickets : {{name}}', { name: row[nameKey] })}
              title={t('clientsUi.detail.seeTicketsOf', 'Voir les tickets : {{name}}', { name: row[nameKey] })}
            >
              <ChevronRight aria-hidden="true" />
            </button>
          ) : <span className="client-b2b-v2__meter-spacer" />}
        </li>
      ))}
    </ul>
  );

  const renderOverview = () => {
    if (stats === null) {
      return <Banner tone="warning">{t('clientsUi.detail.statsError', 'L’activité de ce client est indisponible pour le moment.')}</Banner>;
    }
    if (stats === undefined) {
      return <div className="u-grid-2" aria-hidden="true"><span className="u-skeleton client-b2b-v2__card-skeleton" /><span className="u-skeleton client-b2b-v2__card-skeleton" /></div>;
    }
    if (!activity || activity.total === 0) {
      return (
        <section className="u-card">
          <EmptyState
            icon={Ticket}
            title={t('clientEDNDetail.noActivity', 'Aucun ticket pour ce client.')}
            action={(
              <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => setTab('configuration')}>
                {t('clientsUi.detail.checkRecognition', 'Vérifier sa reconnaissance')}
              </button>
            )}
          >
            {t('clientsUi.detail.noActivityHint', 'Un ticket lui est rattaché quand il vient de ses adresses ou domaines, porte son identifiant client ou un de ses alias.')}
          </EmptyState>
        </section>
      );
    }
    return (
      <div className="client-b2b-v2__stack">
        <div className="u-grid-2 client-b2b-v2__grid">
          <Card
            icon={ListChecks}
            title={t('clientsUi.detail.statusTitle', 'Où en sont ses tickets')}
            description={t('clientsUi.detail.statusDesc', '{{total}} tickets depuis le début · cliquez un statut pour voir ses tickets.', { total: formatCount(activity.total) })}
            flush
          >
            <div className="client-b2b-v2__pad">
              <div className="u-stack" role="img" aria-label={activity.buckets.map((row) => `${bucketLabels[row.bucket]} ${row.count}`).join(', ')}>
                {activity.buckets.map((row) => (
                  <span key={row.bucket} className={`client-b2b-v2__seg--${row.bucket}`} style={{ width: `${(row.count / activity.total) * 100}%` }} />
                ))}
              </div>
              <div className="u-legend client-b2b-v2__legend">
                {activity.buckets.map((row) => (
                  <span key={row.bucket} className="u-legend__item">
                    <span className={`u-legend__swatch client-b2b-v2__seg--${row.bucket}`} />
                    {bucketLabels[row.bucket]} · {formatCount(row.count)}
                  </span>
                ))}
              </div>
            </div>
            <ul className="u-list">
              {activity.byStatus.slice(0, 8).map((row) => (
                <li key={row.status} className="u-row client-b2b-v2__status-row">
                  <Badge tone={statusTone(row.status)} dot>{getStatusLabel(t, row.status)}</Badge>
                  <span className="client-b2b-v2__status-count u-num">{formatCount(row.count)}</span>
                  <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => openTicketsFiltered({ tstatus: row.status })}>
                    {t('clientsUi.see', 'Voir')}
                    <ChevronRight aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
            {activity.byStatus.length > 8 && (
              <div className="client-b2b-v2__others">
                {t('clientsUi.detail.otherStatuses', '+ {{count}} autres statuts', { count: activity.byStatus.length - 8 })}
              </div>
            )}
          </Card>
          <div className="client-b2b-v2__stack">
            {activity.carriers.length > 0 && (
              <Card
                icon={Truck}
                title={t('clientEDNDetail.topCarriers', 'Principaux transporteurs')}
                description={t('clientsUi.detail.carriersDesc', 'Tickets par transporteur · cliquez pour les voir.')}
                flush
              >
                {renderMeterRows(activity.carriers, activity.carriersMax, 'carrier_name', (name) => openTicketsFiltered({ tcarrier: name }))}
              </Card>
            )}
            {activity.topics.length > 0 && (
              <Card
                icon={Tags}
                title={t('clientsUi.detail.topicsTitle', 'Motifs')}
                description={t('clientsUi.detail.topicsDesc', 'Pourquoi ce client vous écrit.')}
                flush
              >
                {renderMeterRows(activity.topics, activity.topicsMax, 'topic')}
              </Card>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTickets = () => {
    const anyFilter = Boolean(statusFilter || carrierFilter);
    let body;
    if (tickets === null) {
      body = (
        <div className="client-b2b-v2__skeleton" aria-busy="true">
          {Array.from({ length: 8 }, (_, i) => <span key={i} className="u-skeleton" />)}
        </div>
      );
    } else if (ticketsError) {
      body = (
        <div className="client-b2b-v2__pad">
          <Banner tone="danger" actions={<button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={fetchTickets}>{t('clientsUi.retry', 'Réessayer')}</button>}>
            {ticketsError}
          </Banner>
        </div>
      );
    } else if (!tickets.length) {
      body = anyFilter ? (
        <EmptyState
          icon={Search}
          title={t('clientsUi.tickets.noMatch', 'Aucun ticket ne correspond')}
          action={(
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => updateParams({ tstatus: null, tcarrier: null, tpage: null })}>
              {t('clientsUi.clearFilters', 'Effacer les filtres')}
            </button>
          )}
        />
      ) : (
        <EmptyState icon={Ticket} title={t('clientEDNDetail.noTickets', 'Aucun ticket trouvé pour ce client')}>
          {t('clientsUi.detail.noActivityHint', 'Un ticket lui est rattaché quand il vient de ses adresses ou domaines, porte son identifiant client ou un de ses alias.')}
        </EmptyState>
      );
    } else {
      body = (
        <div className="u-table-scroll">
          <table className="u-table client-b2b-v2__tickets">
            <thead>
              <tr>
                <th style={{ width: '36%' }}>{t('clientsUi.tickets.colTicket', 'Ticket')}</th>
                <th style={{ width: '15%' }}>{t('clientsUi.tickets.colCarrier', 'Transporteur')}</th>
                <th style={{ width: '17%' }}>{t('clientsUi.tickets.colTopic', 'Motif')}</th>
                <th style={{ width: '20%' }}>{t('clientsUi.tickets.colStatus', 'Statut')}</th>
                <th style={{ width: '12%' }}>{t('clientsUi.tickets.colUpdated', 'Mis à jour')}</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => {
                const byAi = getResolutionActor(ticket) === RESOLUTION_ACTOR.AI;
                return (
                  <tr
                    key={ticket.id}
                    className="client-b2b-v2__row"
                    tabIndex={0}
                    onClick={() => openTicket(ticket.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openTicket(ticket.id);
                      }
                    }}
                  >
                    <td title={ticket.subject || ''}>
                      <span className="client-b2b-v2__subject">{ticket.subject || t('clientEDNDetail.noSubject', 'Pas de sujet')}</span>
                      {ticket.tracking_number && <span className="u-cell-sub client-b2b-v2__mono">{ticket.tracking_number}</span>}
                    </td>
                    <td title={ticket.carrier_name || ''}>{ticket.carrier_name || <span className="u-muted">—</span>}</td>
                    <td title={ticket.topic || ''}>{ticket.topic || <span className="u-muted">—</span>}</td>
                    <td>
                      <span className="client-b2b-v2__badges">
                        <Badge tone={statusTone(ticket.status)} dot>{getStatusLabel(t, ticket.status)}</Badge>
                        {byAi && (
                          <span className="u-badge u-badge--accent client-b2b-v2__ai" role="img" title={t('clientsUi.tickets.byAi', 'Résolu par l’agent IA')} aria-label={t('clientsUi.tickets.byAi', 'Résolu par l’agent IA')}>
                            <Sparkles aria-hidden="true" />
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="u-num" title={formatStamp(ticket.updated_at)}>{formatRelative(ticket.updated_at) || <span className="u-muted">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
    return (
      <section className="u-card">
        <div className="u-toolbar">
          <select
            className="u-select client-b2b-v2__status-select"
            value={statusFilter}
            onChange={(e) => updateParams({ tstatus: e.target.value, tpage: null })}
            aria-label={t('clientsUi.tickets.statusFilter', 'Filtrer par statut')}
          >
            <option value="">{t('allStatuses', 'Tous les statuts')}</option>
            {ALL_STATUS_MAPPING.map(({ value }) => (
              <option key={value} value={value}>{getStatusLabel(t, value)}</option>
            ))}
          </select>
          <div className="u-input-group u-input-group--lead">
            <Truck className="u-input-group__icon" aria-hidden="true" />
            <input
              type="search"
              className="u-input"
              value={carrierInput}
              onChange={(e) => setCarrierInput(e.target.value)}
              placeholder={t('clientEDNDetail.filterCarrier', 'Filtrer par transporteur...')}
              aria-label={t('clientsUi.tickets.carrierFilter', 'Filtrer par transporteur')}
            />
          </div>
          {anyFilter && (
            <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => updateParams({ tstatus: null, tcarrier: null, tpage: null })}>
              <X aria-hidden="true" />
              {t('clientsUi.clearFilters', 'Effacer les filtres')}
            </button>
          )}
          <span className="u-toolbar__spacer" />
          {ticketsTotal !== null && tickets !== null && (
            <span className="client-b2b-v2__count">
              {t('clientsUi.tickets.count', '{{count}} tickets', { count: formatCount(ticketsTotal) })}
            </span>
          )}
        </div>
        {body}
        {tickets && tickets.length > 0 && (ticketsTotal !== null ? (
          <Pagination
            page={ticketsPage}
            pageSize={TICKETS_PAGE_SIZE}
            total={ticketsTotal}
            onChange={(next) => updateParams({ tpage: next <= 1 ? null : next }, { replace: false })}
            summary={({ first, last, total }) => t('clientsUi.pageSummary', '{{first}}–{{last}} sur {{total}}', { first, last, total: formatCount(total) })}
            previousLabel={t('common.previousPage', 'Page précédente')}
            nextLabel={t('common.nextPage', 'Page suivante')}
          />
        ) : (ticketsPage > 1 || ticketsHasMore) && (
          <div className="u-pagination">
            <span>{t('clientsUi.pageN', 'Page {{page}}', { page: ticketsPage })}</span>
            <div className="u-pagination__pages">
              <button type="button" className="u-btn u-btn--secondary u-btn--sm" disabled={ticketsPage <= 1} onClick={() => updateParams({ tpage: ticketsPage - 1 <= 1 ? null : ticketsPage - 1 }, { replace: false })}>
                {t('clientsUi.previous', 'Précédente')}
              </button>
              <button type="button" className="u-btn u-btn--secondary u-btn--sm" disabled={!ticketsHasMore} onClick={() => updateParams({ tpage: ticketsPage + 1 }, { replace: false })}>
                {t('clientsUi.next', 'Suivante')}
              </button>
            </div>
          </div>
        ))}
      </section>
    );
  };

  const renderConfiguration = () => {
    if (editing && editForm) {
      return (
        <form className="client-b2b-v2__edit" onSubmit={handleSave} noValidate>
          {saveError && <Banner tone="danger">{saveError}</Banner>}
          <ClientB2BForm
            form={editForm}
            setForm={setEditForm}
            carriers={carriers}
            client={client}
            layout="cards"
            idPrefix="edit-client"
            generalLanguage={generalLanguage}
          />
          <div className="u-card client-b2b-v2__savebar">
            <span className="client-b2b-v2__savebar-note">
              {t('clientsUi.detail.saveNote', 'Les changements s’appliquent aux prochains tickets de ce client.')}
            </span>
            <button type="button" className="u-btn u-btn--secondary" onClick={cancelEdit} disabled={saving}>
              {t('common.cancel', 'Annuler')}
            </button>
            <button type="submit" className="u-btn u-btn--primary" disabled={saving || !editForm.name.trim()}>
              {saving ? t('common.saving', 'Enregistrement...') : t('common.save', 'Enregistrer')}
            </button>
          </div>
        </form>
      );
    }
    return (
      <ClientB2BPreview client={client} carriers={carriers} generalLanguage={generalLanguage} onOpenTicket={openTicket} />
    );
  };

  const renderApi = () => (
    <Card
      icon={Plug}
      title={t('clientEDNDetail.apiAccess', 'Accès API ERP')}
      badge={apiAccess ? (apiAccess.enabled
        ? <Badge tone="success" dot>{t('clientEDNDetail.apiActive', 'Actif')}</Badge>
        : <Badge>{t('clientEDNDetail.apiInactive', 'Inactif')}</Badge>) : null}
      description={t('clientEDNDetail.apiAccessDescription', 'Activez une clé API privée pour que l\'ERP de ce client crée, consulte et mette à jour ses tickets directement. Les tickets créés ainsi sont routés vers l\'IA comme n\'importe quel autre canal.')}
      footerNote={apiAccess?.enabled ? t('clientsUi.api.footerOn', 'Régénérer ou révoquer coupe l’ancienne clé immédiatement.') : undefined}
      footer={apiAccessLoading ? null : apiAccess?.enabled ? (
        <>
          <button type="button" className="u-btn u-btn--danger" onClick={() => setConfirmation('revoke')} disabled={apiAccessBusy}>
            {t('clientsUi.api.revoke', 'Révoquer…')}
          </button>
          <button type="button" className="u-btn u-btn--secondary" onClick={() => setConfirmation('rotate')} disabled={apiAccessBusy}>
            <RefreshCw aria-hidden="true" />
            {t('clientsUi.api.rotate', 'Régénérer la clé…')}
          </button>
        </>
      ) : (
        <button type="button" className="u-btn u-btn--primary" onClick={handleGenerateApiKey} disabled={apiAccessBusy}>
          <KeyRound aria-hidden="true" />
          {apiAccessBusy ? t('common.saving', 'Enregistrement...') : t('clientEDNDetail.apiActivate', 'Activer l\'accès API')}
        </button>
      )}
    >
      <div className="client-b2b-v2__api">
        {apiAccessError && <Banner tone="danger">{apiAccessError}</Banner>}
        {apiAccessLoading ? (
          <div className="client-b2b-v2__skeleton client-b2b-v2__skeleton--flat" aria-busy="true">
            <span className="u-skeleton" />
            <span className="u-skeleton" />
          </div>
        ) : (
          <>
            {generatedApiKey && (
              <Banner tone="warning" title={t('clientEDNDetail.apiKeyWarning', 'Copiez cette clé maintenant — elle n\'est affichée qu\'une seule fois.')}>
                <div className="client-b2b-v2__key">
                  <code>{generatedApiKey}</code>
                  <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={handleCopyApiKey}>
                    {apiKeyCopied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
                    {apiKeyCopied ? t('clientsUi.api.copied', 'Copiée') : t('clientsUi.api.copy', 'Copier')}
                  </button>
                </div>
              </Banner>
            )}
            {apiAccess && (apiAccess.key_prefix || apiAccess.created_at || apiAccess.revoked_at) && (
              <dl className="u-dl">
                {apiAccess.key_prefix && (
                  <div>
                    <dt>{t('clientsUi.api.key', 'Clé')}</dt>
                    <dd><span className="u-code">{`${apiAccess.key_prefix}••••••••`}</span></dd>
                  </div>
                )}
                {apiAccess.created_at && (
                  <div>
                    <dt>{t('clientEDNDetail.apiCreatedAt', 'Créée le')}</dt>
                    <dd>{formatStamp(apiAccess.created_at)}</dd>
                  </div>
                )}
                <div>
                  <dt>{t('clientEDNDetail.apiLastUsed', 'Dernière utilisation')}</dt>
                  <dd>{apiAccess.last_used_at ? formatStamp(apiAccess.last_used_at) : <span className="u-muted">{t('clientsUi.api.never', 'Jamais')}</span>}</dd>
                </div>
                {apiAccess.revoked_at && (
                  <div>
                    <dt>{t('clientsUi.api.revokedAt', 'Révoquée le')}</dt>
                    <dd>{formatStamp(apiAccess.revoked_at)}</dd>
                  </div>
                )}
              </dl>
            )}
            {apiAccess?.enabled && (
              <details className="u-disclosure client-b2b-v2__endpoints">
                <summary>
                  <ChevronRight aria-hidden="true" />
                  {t('clientsUi.api.advanced', 'Avancé : adresses de l’API pour l’intégrateur')}
                </summary>
                <div className="client-b2b-v2__endpoints-body">
                  <p className="u-help">
                    {t('clientEDNDetail.apiEndpointsHelp', "Envoyez la clé dans l'en-tête")} <span className="u-code">X-API-Key</span>
                  </p>
                  <ul className="client-b2b-v2__endpoint-list">
                    {[
                      ['POST', '/api/integrations/tickets'],
                      ['GET', '/api/integrations/tickets'],
                      ['GET', '/api/integrations/tickets/{ticket_id}'],
                      ['PATCH', '/api/integrations/tickets/{ticket_id}'],
                    ].map(([verb, path]) => (
                      <li key={`${verb} ${path}`}>
                        <Badge tone={verb === 'GET' ? 'info' : 'accent'}>{verb}</Badge>
                        <code>{apiBaseUrl}{path}</code>
                      </li>
                    ))}
                  </ul>
                </div>
              </details>
            )}
          </>
        )}
      </div>
    </Card>
  );

  const confirmProps = confirmation === 'revoke'
    ? {
      title: t('clientsUi.api.revokeTitle', 'Révoquer l’accès API de {{name}} ?', { name: client?.name }),
      message: t('clientEDNDetail.apiRevokeConfirm', 'Révoquer l\'accès API de ce client ? Son intégration ERP cessera de fonctionner immédiatement.'),
      confirmLabel: t('clientEDNDetail.apiRevoke', 'Révoquer'),
      onConfirm: handleRevokeApiKey,
    }
    : {
      title: t('clientsUi.api.rotateTitle', 'Régénérer la clé de {{name}} ?', { name: client?.name }),
      message: t('clientsUi.api.rotateMessage', 'L’ancienne clé cesse de fonctionner immédiatement : l’ERP de ce client devra recevoir la nouvelle.'),
      confirmLabel: t('clientEDNDetail.apiRotate', 'Régénérer la clé'),
      onConfirm: handleGenerateApiKey,
    };

  return (
    <div className="u-page client-b2b-v2">
      {clientError && !client ? (
        <section className="u-card">
          <EmptyState
            icon={CircleAlert}
            title={clientError}
            action={(
              <Link className="u-btn u-btn--secondary u-btn--sm" to="/clients-instructions">
                {t('clientEDNDetail.backToB2B', 'Retour aux clients B2B')}
              </Link>
            )}
          />
        </section>
      ) : (
        <>
          {renderHero()}
          {client && (
            <div className="u-well">
              {tab === 'overview' && (
                <>
                  {renderAttention()}
                  {renderKpis()}
                </>
              )}
              <div role="tabpanel">
                {tab === 'overview' && renderOverview()}
                {tab === 'tickets' && renderTickets()}
                {tab === 'configuration' && renderConfiguration()}
                {tab === 'api' && renderApi()}
              </div>
            </div>
          )}
        </>
      )}

      {confirmation && (
        <ConfirmDialog
          {...confirmProps}
          cancelLabel={t('common.cancel', 'Annuler')}
          onCancel={() => setConfirmation(null)}
          busy={apiAccessBusy}
        />
      )}

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
          {toast.text}
        </Toast>
      )}
    </div>
  );
};

export default ClientEDNDetail;
